import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Calendar,
  Check,
  Coffee,
  Loader2,
  MonitorPlay,
  TrendingUp,
  Upload,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const ROTATION_MS = 3000;

interface StepDef {
  id: 'idea' | 'build' | 'publish';
  title: string;
  description: string;
}

const STEPS: StepDef[] = [
  {
    id: 'idea',
    title: 'Just say what you need',
    description:
      'Describe what you want on your screens, in plain words. Add a file or paste a link if you\'d like to.',
  },
  {
    id: 'build',
    title: 'Watch it take shape',
    description:
      'OptiDev wires up the layout, data, and visuals in real time — built for the screen you described.',
  },
  {
    id: 'publish',
    title: 'Push it to your screens',
    description:
      'One click and it goes live on every OptiSigns display you choose. Iterate any time without touching code.',
  },
];

/* ─────────────────────── Use cases ───────────────────────
   Each use case ships its own bespoke kiosk component so the demos look
   like genuinely different products (Stripe-style sales dashboard, a Blue
   Bottle-ish cafe lobby, an Apple-keynote slide, a clinic check-in display)
   rather than the same template re-skinned. The modal picks one at random
   per open — close + replay shows a different demo. */

type AppProps = { compact: boolean; animated: boolean };

interface UseCase {
  id: string;
  /** Short label used by the inline HowItWorksSection's pill tabs. */
  label: string;
  promptText: string;
  App: React.FC<AppProps>;
}

const USE_CASES: UseCase[] = [
  {
    id: 'sales',
    label: 'Dashboard',
    promptText:
      'Turn this messy sales spreadsheet into a live dashboard for our store managers',
    App: SalesApp,
  },
  {
    id: 'storefront',
    label: 'Welcome screen',
    promptText:
      'Pull our site acmecoffee.com and build a lobby welcome screen for the cafe',
    App: CafeApp,
  },
  {
    id: 'allhands',
    label: 'Presentation',
    promptText:
      'Turn our quarterly all-hands highlights into a polished office display',
    App: AllHandsApp,
  },
  {
    id: 'patient',
    label: 'Kiosk',
    promptText:
      'A patient waiting room kiosk that shows the live queue and wait times',
    App: PatientApp,
  },
];

const USE_CASE_LAST_KEY = 'optidev:meet-modal-usecase-last';

/** Pick a random use case, avoiding the one shown last time (per browser). */
function pickUseCase(): UseCase {
  let lastIdx = -1;
  try {
    const raw = localStorage.getItem(USE_CASE_LAST_KEY);
    if (raw) {
      const parsed = parseInt(raw, 10);
      if (!Number.isNaN(parsed)) lastIdx = parsed;
    }
  } catch {
    // localStorage unavailable — fall through to plain random.
  }
  let nextIdx = Math.floor(Math.random() * USE_CASES.length);
  if (USE_CASES.length > 1) {
    while (nextIdx === lastIdx) {
      nextIdx = Math.floor(Math.random() * USE_CASES.length);
    }
  }
  try {
    localStorage.setItem(USE_CASE_LAST_KEY, String(nextIdx));
  } catch {
    // best-effort
  }
  return USE_CASES[nextIdx];
}

/** Look up a use case by id (e.g. industry-featured). Returns undefined when
 *  the id is empty or doesn't match a known demo. */
function findUseCaseById(id?: string): UseCase | undefined {
  if (!id) return undefined;
  return USE_CASES.find((uc) => uc.id === id);
}

/** Pick the featured use case when provided & valid, else fall back to random. */
function pickUseCaseFor(featuredId?: string): UseCase {
  return findUseCaseById(featuredId) ?? pickUseCase();
}

/** Hold the same use case for the lifetime of an open session; pick a new
 *  one each time the modal transitions closed → open.
 *
 *  Picks synchronously during render on the closed→open transition so the
 *  first render after `open=true` already has the picked use case. This
 *  ensures downstream effects (like analytics `onShown`) read the final
 *  picked value, not the initial USE_CASES[0] fallback. When `featuredId`
 *  matches a known demo it's used; otherwise the demo is random. */
function useUseCase(open: boolean, featuredId?: string): UseCase {
  const [useCase, setUseCase] = useState<UseCase>(() =>
    open ? pickUseCaseFor(featuredId) : USE_CASES[0],
  );
  const wasOpenRef = useRef(open);

  if (open !== wasOpenRef.current) {
    wasOpenRef.current = open;
    if (open) {
      const next = pickUseCaseFor(featuredId);
      setUseCase(next);
      return next;
    }
  }

  return useCase;
}

/** Inline fade-up style — used by every use case App for staggered enter
 *  animations. Returns `undefined` when animations are off (compact view in
 *  the publish scene), so the published kiosk doesn't re-animate as the
 *  screen "powers on". */
function fadeUp(delay: number, animated: boolean): React.CSSProperties | undefined {
  if (!animated) return undefined;
  return { animation: `meetFadeUp 360ms cubic-bezier(0.2, 0, 0, 1) ${delay}ms both` };
}

interface MeetOptiDevModalProps {
  open: boolean;
  onClose: () => void;
  /** When set and it matches a known use case (sales | storefront | allhands |
   *  patient), the modal features that demo instead of picking randomly. Used
   *  to align the intro demo with the embed user's industry. */
  featuredUseCaseId?: string;
  /** Fired when the modal becomes visible. Receives the use case the internal
   *  pickUseCase selected. */
  onShown?: (useCaseId: string) => void;
  /** Fired when the user clicks the "Got it — let's build" CTA button.
   *  Receives the use case shown and the current step (0/1/2). */
  onCtaClick?: (useCaseId: string, stepIndex: number) => void;
  /** Fired when the user closes the modal via X, Escape, or backdrop click
   *  (NOT the CTA). Receives the use case shown and the current step. */
  onDismiss?: (useCaseId: string, stepIndex: number) => void;
  /** Category thumbnail (a screenshot of a real deployed signage app). When
   *  provided, the build + publish steps show this image. The parent re-rolls
   *  it each time the modal opens. */
  thumbnailUrl?: string;
  /** The prompt that produced `thumbnailUrl` — typed in the Idea step so it
   *  matches the screenshot shown in the later steps. */
  promptText?: string;
  /** Endpoint-screen names for the publish step (industry-specific spots a
   *  display lands in). Defaults to a generic trio. */
  screenNames?: string[];
}

export function MeetOptiDevModal({ open, onClose, featuredUseCaseId, onShown, onCtaClick, onDismiss, thumbnailUrl, promptText, screenNames }: MeetOptiDevModalProps): JSX.Element | null {
  const useCase = useUseCase(open, featuredUseCaseId);
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRotation = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % STEPS.length);
    }, ROTATION_MS);
  }, []);

  const stopRotation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Fire onShown at most once per closed→open transition. Reset on close so
  // the next open fires again. Defensive — `useUseCase` now picks during
  // render, but this ensures onShown never duplicates even if the picking
  // strategy changes later.
  const onShownFiredRef = useRef(false);

  // Reset to first step + start rotation each time the modal opens.
  useEffect(() => {
    if (!open) {
      stopRotation();
      onShownFiredRef.current = false;
      return;
    }
    setActiveIndex(0);
    startRotation();
    if (!onShownFiredRef.current) {
      onShownFiredRef.current = true;
      onShown?.(useCase.id);
    }
    return stopRotation;
  }, [open, startRotation, stopRotation, onShown, useCase.id]);

  const handleCtaClick = useCallback(() => {
    onCtaClick?.(useCase.id, activeIndex);
    onClose();
  }, [onCtaClick, useCase.id, activeIndex, onClose]);

  const handleDismiss = useCallback(() => {
    onDismiss?.(useCase.id, activeIndex);
    onClose();
  }, [onDismiss, useCase.id, activeIndex, onClose]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleDismiss]);

  const handleStepClick = useCallback(
    (i: number) => {
      setActiveIndex(i);
      startRotation();
    },
    [startRotation],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      style={{ animation: 'meetBackdropIn 200ms ease-out both' }}
      onClick={handleDismiss}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="meet-optidev-title"
        className="relative w-full max-w-[1080px] overflow-hidden rounded-3xl border border-border bg-card shadow-2xl"
        style={{ animation: 'meetModalIn 280ms cubic-bezier(0.2, 0, 0, 1) both' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Aurora glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: [
              'radial-gradient(700px circle at 8% -10%, rgba(168, 85, 247, 0.22), transparent 60%)',
              'radial-gradient(600px circle at 100% 110%, rgba(99, 102, 241, 0.18), transparent 60%)',
              'radial-gradient(500px circle at 60% 50%, rgba(236, 72, 153, 0.10), transparent 60%)',
            ].join(', '),
          }}
        />

        {/* Close */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-muted/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="relative px-8 pb-2 pt-7 sm:px-10 sm:pt-8">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
            Welcome
          </p>
          <h2
            id="meet-optidev-title"
            className="mt-1.5 text-2xl font-semibold tracking-tight sm:text-[28px]"
          >
            Say hello to OptiDev
          </h2>
        </div>

        {/* Body */}
        <div className="relative grid grid-cols-1 gap-6 px-8 pb-2 pt-4 sm:px-10 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-7">
            <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-gradient-to-br from-white to-zinc-50 ring-1 ring-black/5 dark:from-zinc-900 dark:to-zinc-950 dark:ring-white/10">
              <Stage
                activeIndex={activeIndex}
                useCase={useCase}
                thumbnailUrl={thumbnailUrl}
                promptText={promptText}
                screenNames={screenNames}
              />
            </div>
          </div>

          <div className="flex flex-col justify-center gap-7 md:col-span-5">
            {STEPS.map((step, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handleStepClick(i)}
                  className={cn(
                    'group text-left transition-opacity duration-300',
                    isActive ? 'opacity-100' : 'opacity-40 hover:opacity-70',
                  )}
                >
                  <h3
                    className={cn(
                      'text-xl font-semibold tracking-tight transition-colors sm:text-[22px]',
                      isActive ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {/* Inline-block wrapper so the brush highlight sizes to
                        the title text width, not the full button width. */}
                    <span className="relative inline-block">
                      {isActive && (
                        <span
                          key={`${step.id}-${activeIndex}-brush`}
                          aria-hidden
                          className="pointer-events-none absolute inset-x-[-4px] bottom-[-2px] top-[42%] origin-left"
                          style={{
                            // Layered gradient gives the band a "wet" leading
                            // edge that's slightly more saturated, like a
                            // marker laying down ink as it sweeps right.
                            background:
                              'linear-gradient(100deg, rgba(147, 51, 234, 0.40) 0%, rgba(147, 51, 234, 0.55) 60%, rgba(147, 51, 234, 0.65) 100%)',
                            // Asymmetric radii give organic, hand-drawn ends.
                            borderRadius: '3px 10px 4px 12px',
                            transform: 'skewX(-4deg) scaleX(0)',
                            animation: `meetPaintStroke ${ROTATION_MS / 3}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                            zIndex: 0,
                          }}
                        />
                      )}
                      {/* Text rendered on top of the brush via z-index. */}
                      <span className="relative" style={{ zIndex: 1 }}>
                        {step.title}
                      </span>
                    </span>
                  </h3>
                  <p
                    className={cn(
                      'mt-1.5 text-sm leading-relaxed text-muted-foreground transition-colors sm:text-[15px]',
                      !isActive && 'text-muted-foreground/70',
                    )}
                  >
                    {step.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="relative mt-2 flex items-center justify-between gap-4 border-t border-border/50 bg-card/40 px-8 py-4 sm:px-10">
          <p className="hidden text-xs text-muted-foreground sm:block">
            100 free credits · 14-day trial · Publish to any OptiSigns screen
          </p>
          <button
            type="button"
            onClick={handleCtaClick}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700"
          >
            See more examples
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Self-contained keyframes — colocated with the modal so a redesign
          here doesn't accidentally collide with global animation names. */}
      <style>{`
        @keyframes meetBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes meetModalIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes meetProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes meetPaintStroke {
          from { transform: skewX(-4deg) scaleX(0); }
          to   { transform: skewX(-4deg) scaleX(1); }
        }
        @keyframes meetFadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes meetFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes meetFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes meetCaret {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes meetCursorClick {
          0% { transform: translate(0, 0) scale(1); }
          70% { transform: translate(0, 0) scale(1); }
          80% { transform: translate(0, 0) scale(0.85); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes meetButtonPress {
          0%, 70% { transform: scale(1); box-shadow: 0 0 0 0 rgba(168, 85, 247, 0); }
          80% { transform: scale(0.97); box-shadow: 0 0 0 6px rgba(168, 85, 247, 0.18); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(168, 85, 247, 0); }
        }
        @keyframes meetScreenPower {
          from { opacity: 0; filter: brightness(0.4); }
          to { opacity: 1; filter: brightness(1); }
        }
        /* ── Publish → Live morph ──
           The button stays in place and transforms through state instead of
           swapping out — purple→emerald bg flash, blur-fade text crossfade,
           outward success ring pulse. Inspired by Stripe's payment confirm
           and Apple Pay's success animation. */
        @keyframes meetButtonMorph {
          0% { background-color: rgb(147, 51, 234); }
          100% { background-color: rgb(16, 185, 129); }
        }
        @keyframes meetTextOut {
          from { opacity: 1; filter: blur(0); }
          to { opacity: 0; filter: blur(2px); }
        }
        @keyframes meetTextIn {
          from { opacity: 0; transform: translateY(6px); filter: blur(2px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes meetSuccessRing {
          0% {
            box-shadow:
              0 0 0 0 rgba(16, 185, 129, 0.55),
              0 10px 30px -10px rgba(16, 185, 129, 0.45);
          }
          100% {
            box-shadow:
              0 0 0 22px rgba(16, 185, 129, 0),
              0 6px 20px -10px rgba(16, 185, 129, 0.18);
          }
        }
        /* Width expand — pill grows from "Publish" width to "Live on 3 screens"
           width during the morph. Pairs with the bg / text crossfade so the
           container feels like it's transforming, not swapping. */
        @keyframes meetButtonExpand {
          from { width: 112px; }
          to { width: 168px; }
        }
        /* Stroke-draw the success ✓ — animates stroke-dashoffset from full
           path length to 0 so the check appears to draw itself in. */
        @keyframes meetCheckDraw {
          from { stroke-dashoffset: 24; }
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────── Stage ─────────────────────────── */

function Stage({
  activeIndex,
  useCase,
  thumbnailUrl,
  promptText,
  screenNames,
}: {
  activeIndex: number;
  useCase: UseCase;
  thumbnailUrl?: string;
  promptText?: string;
  screenNames?: string[];
}): JSX.Element {
  // All three scenes stay mounted — that lets the outgoing scene fade out
  // with its actual content visible (proper cross-fade) instead of snapping
  // to an empty container. Each scene gates / replays its own animations
  // off `active`.
  return (
    <div className="relative h-full w-full">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'absolute inset-0 transition-opacity duration-200 ease-out',
            i === activeIndex ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          aria-hidden={i !== activeIndex}
        >
          <SceneByIndex
            index={i}
            active={i === activeIndex}
            useCase={useCase}
            thumbnailUrl={thumbnailUrl}
            promptText={promptText}
            screenNames={screenNames}
          />
        </div>
      ))}
    </div>
  );
}

function SceneByIndex({
  index,
  active,
  useCase,
  thumbnailUrl,
  promptText,
  screenNames,
}: {
  index: number;
  active: boolean;
  useCase: UseCase;
  thumbnailUrl?: string;
  promptText?: string;
  screenNames?: string[];
}): JSX.Element {
  // Step 1 types the prompt that produced the screenshot; steps 2 (build) and
  // 3 (publish) show that real category thumbnail in their browser / on-screen
  // formats.
  if (index === 0)
    return <IdeaScene active={active} useCase={useCase} promptText={promptText} />;
  if (index === 1)
    return <BuildScene active={active} useCase={useCase} thumbnailUrl={thumbnailUrl} />;
  return (
    <PublishScene
      active={active}
      useCase={useCase}
      thumbnailUrl={thumbnailUrl}
      screenNames={screenNames}
    />
  );
}

/** Replay-key that increments each time `active` flips false → true. Used
 *  as a `key` on a scene's content wrapper to remount the subtree and
 *  restart any CSS keyframe animations declared inside it. */
function useReplayKey(active: boolean): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (active) setVersion((v) => v + 1);
  }, [active]);
  return version;
}

/* ──────────────────── Scene 1 — Start with an idea ──────────────────── */

function IdeaScene({
  active,
  useCase,
  promptText,
}: {
  active: boolean;
  useCase: UseCase;
  promptText?: string;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  const replayKey = useReplayKey(active);
  // Prefer the category prompt (matches the screenshot in later steps); fall
  // back to the use case's generic prompt.
  const prompt = promptText ?? useCase.promptText;

  useEffect(() => {
    if (!active) {
      setTyped('');
      return;
    }
    // Per-char delay scales with prompt length so every demo's typing lands
    // at roughly the same beat (~2s) regardless of how long the prompt is.
    // Press + cursor click animations then fill the remaining ~800ms.
    const speed = Math.max(20, Math.min(40, Math.round(2050 / prompt.length)));
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(prompt.slice(0, i));
      if (i >= prompt.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [active, prompt]);

  const isDone = typed.length === prompt.length;

  return (
    <div className="flex h-full w-full items-center justify-center p-6 sm:p-10">
      <div
        key={replayKey}
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-xl"
        style={{ animation: 'meetFadeUp 280ms cubic-bezier(0.2, 0, 0, 1) both' }}
      >
        <div className="px-5 pt-5">
          <div className="min-h-[88px] text-[15px] leading-relaxed text-foreground">
            {typed}
            <span
              aria-hidden
              className="ml-0.5 inline-block h-4 w-[2px] -translate-y-[1px] bg-foreground align-middle"
              style={{ animation: 'meetCaret 1s steps(2) infinite' }}
            />
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between border-t border-border/40 px-3 py-2.5">
          <span className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-muted-foreground">
            <Upload className="h-3.5 w-3.5" />
            Attach
          </span>
          {/* Build-it button + faux cursor click — cursor mounts when typing
              completes and visually clicks the button just before the slide
              auto-rotates, so the build feels like the user's gesture. */}
          <div className="relative">
            <div
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-sm font-medium transition-all',
                isDone ? 'bg-purple-600 text-white' : 'bg-muted text-muted-foreground',
              )}
              style={isDone ? { animation: 'meetButtonPress 700ms ease-out 100ms both' } : undefined}
            >
              Build it
              <ArrowRight className="h-3.5 w-3.5" />
            </div>
            {isDone && (
              <svg
                viewBox="0 0 24 24"
                aria-hidden
                className="pointer-events-none absolute -bottom-1 right-2 h-4 w-4 text-foreground drop-shadow"
                style={{ animation: 'meetCursorClick 700ms ease-out 100ms both' }}
              >
                <path
                  d="M5 3l14 9-7 1 4 8-3 1-4-8-4 5V3z"
                  fill="currentColor"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── Scene 2 — Watch it come to life ──────────────────── */

function BuildScene({
  active,
  useCase,
  thumbnailUrl,
}: {
  active: boolean;
  useCase: UseCase;
  thumbnailUrl?: string;
}): JSX.Element {
  const replayKey = useReplayKey(active);
  const App = useCase.App;
  return (
    <div key={replayKey} className="flex h-full w-full items-center justify-center p-5 sm:p-7">
      {/* Scale wrapper — zooms the whole browser frame down uniformly so the
          chrome bar + app body fit the stage with consistent margin on all
          sides, regardless of which use case is rendered. The fade-up
          animation lives on the inner frame so transforms don't conflict. */}
      <div className="w-full" style={{ transform: 'scale(0.92)' }}>
      <div
        className="w-full overflow-hidden rounded-xl border border-border/60 bg-white shadow-xl dark:border-white/10 dark:bg-zinc-900"
        style={{ animation: 'meetFadeUp 260ms cubic-bezier(0.2, 0, 0, 1) both' }}
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 border-b border-slate-200/70 bg-slate-50/80 px-3 py-2 dark:border-white/10 dark:bg-zinc-800/80">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-400/80" />
          <div className="ml-3 flex items-center gap-1.5 rounded-md bg-white/80 px-2 py-0.5 text-[10px] text-slate-500 dark:bg-slate-800/80 dark:text-slate-400">
            preview.optidev.ai
          </div>
          <div className="ml-auto flex items-center gap-1 text-[10px] font-medium text-purple-600 dark:text-purple-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            Building…
          </div>
        </div>

        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="block aspect-video w-full object-cover object-top"
            loading="lazy"
          />
        ) : (
          <App compact={false} animated />
        )}
      </div>
      </div>
    </div>
  );
}

/* ──────────────────── Scene 3 — Push to your screens ──────────────────── */

function PublishScene({
  active,
  useCase,
  thumbnailUrl,
  screenNames,
}: {
  active: boolean;
  useCase: UseCase;
  thumbnailUrl?: string;
  screenNames?: string[];
}): JSX.Element {
  const replayKey = useReplayKey(active);
  const App = useCase.App;
  // Endpoint screens light up after the morph completes — cause/effect of
  // the publish click landing on each display. Labels are industry-specific
  // (the spots a display lands in), with a generic fallback.
  const names = screenNames ?? ['Lobby', 'Reception', 'Break Room'];
  const screens = useMemo(
    () => [
      { label: names[0] ?? 'Lobby',      delay: 1880 },
      { label: names[1] ?? 'Reception',  delay: 1980 },
      { label: names[2] ?? 'Break Room', delay: 2080 },
    ],
    [names],
  );

  return (
    <div
      key={replayKey}
      className="relative flex h-full w-full items-center justify-center p-5 sm:p-7"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-purple-500/[0.06] to-transparent"
      />

      <div className="relative flex w-full max-w-md flex-col items-center gap-3">
        <MorphingPublishButton />

        {/* TV with the published kiosk — capped width so the 16:9 thumbnail
            screen doesn't grow too tall and push the endpoint screens out. */}
        <div className="mx-auto w-full max-w-[320px]">
          <div className="rounded-[14px] bg-slate-900 p-1.5 shadow-2xl ring-1 ring-black/40 dark:bg-zinc-800 dark:ring-2 dark:ring-white/40">
            <div
              className="overflow-hidden rounded-md"
              style={{ animation: 'meetScreenPower 320ms ease-out 80ms both' }}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt=""
                  className="block aspect-video w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <App compact animated={false} />
              )}
            </div>
          </div>
          {/* Stand — light variants worked, but slate-800 disappeared into
              the zinc-900/950 asset frame in dark mode. Match the bezel's
              zinc palette + a thin white ring so the stand reads in both. */}
          <div className="mx-auto mt-1 h-1.5 w-1/4 rounded-b-md bg-slate-800/80 dark:bg-zinc-700 dark:ring-1 dark:ring-white/20" />
          <div className="mx-auto mt-0.5 h-1 w-1/3 rounded bg-slate-800/60 dark:bg-zinc-700/80 dark:ring-1 dark:ring-white/15" />
        </div>

        {/* Endpoint screens — fade in after the success ring pulses */}
        <div className="mt-1 flex w-full items-center justify-between gap-2">
          {screens.map((s) => (
            <div
              key={s.label}
              className="flex flex-1 items-center gap-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/[0.06] px-2 py-1.5"
              style={{ animation: `meetFadeUp 260ms cubic-bezier(0.2, 0, 0, 1) ${s.delay}ms both` }}
            >
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </div>
              <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Single button that morphs through the publish flow.
 *
 * Timeline (ms from scene mount):
 *   250 – 850   button press + cursor click
 *   700 – 1400  white progress bar fills inside the button
 *   1400 – 1600 "Publish" text+icon blur-fades out
 *   1600 – 1900 background color morphs purple → emerald
 *   1700 – 2000 "Live on 3 screens" blur-fades in
 *   1700 – 2400 success ring pulses outward
 *
 * The button stays in place at 200px wide; cursor sits as a sibling so it
 * isn't clipped by the button's `overflow-hidden`. */
function MorphingPublishButton(): JSX.Element {
  // Timeline (ms from scene mount, with ROTATION_MS=3000):
  //   200 – 700   button press + cursor click
  //   600 – 1200  white progress bar fills inside the button
  //   1100 – 1300 "Publish" text+icon fades + blurs out
  //   1200 – 1520 button width expands (Publish width → Live width)
  //   1300 – 1580 background morphs purple → emerald
  //   1560 – 1840 "Live on 3 screens" fades in (starts AFTER width settles, so
  //               text doesn't fight the expanding container)
  //   1720 – 1960 ✓ icon strokes itself in (Apple-style finishing flourish)
  //   1400 – 2000 success ring pulses outward
  //   1880 – 2320 endpoint pills fade up sequentially
  return (
    <div className="relative">
      <div
        className="relative flex h-8 items-center justify-center overflow-hidden rounded-lg bg-purple-600 text-[13px] font-medium text-white shadow-lg"
        style={{
          width: 112,
          animation: [
            'meetButtonPress 500ms ease-out 200ms both',
            'meetButtonExpand 320ms cubic-bezier(0.2, 0, 0, 1) 1200ms forwards',
            'meetButtonMorph 280ms ease-out 1300ms forwards',
            'meetSuccessRing 600ms ease-out 1400ms forwards',
          ].join(', '),
        }}
      >
        {/* Progress fill — sweeps left-to-right inside the button, then
            fades out as the morph kicks in. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 origin-left bg-white/[0.18]"
          style={{
            animation: [
              'meetProgress 600ms ease-out 600ms forwards',
              'meetFadeOut 240ms ease-out 1200ms forwards',
            ].join(', '),
          }}
        />

        {/* Pre text — "Publish" */}
        <div
          className="absolute inset-0 flex items-center justify-center gap-1.5"
          style={{ animation: 'meetTextOut 200ms ease-in 1100ms forwards' }}
        >
          <MonitorPlay className="h-3.5 w-3.5" strokeWidth={2.25} />
          <span>Publish</span>
        </div>

        {/* Post text — "Live on 3 screens" with stroke-drawn check icon */}
        <div
          className="absolute inset-0 flex items-center justify-center gap-1.5 opacity-0"
          style={{ animation: 'meetTextIn 280ms cubic-bezier(0.2, 0, 0, 1) 1560ms forwards' }}
        >
          {/* Custom check that strokes itself in — uses a single path with
              animated stroke-dashoffset so the ✓ "draws" rather than just
              appearing. The "swish" stays; this is the finishing flourish. */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3.5 w-3.5"
            aria-hidden
          >
            <path
              d="M5 12 l5 5 l9 -10"
              strokeDasharray="24"
              style={{ animation: 'meetCheckDraw 240ms cubic-bezier(0.2, 0, 0, 1) 1720ms both' }}
            />
          </svg>
          <span>Live on 3 screens</span>
        </div>
      </div>

      {/* Faux cursor — sibling to the button so the button's
          overflow-hidden doesn't clip it. */}
      <svg
        viewBox="0 0 24 24"
        aria-hidden
        className="pointer-events-none absolute -bottom-1 right-2 h-4 w-4 text-foreground drop-shadow"
        style={{
          animation: [
            'meetCursorClick 600ms ease-out 200ms both',
            'meetFadeOut 200ms ease-out 800ms forwards',
          ].join(', '),
        }}
      >
        <path
          d="M5 3l14 9-7 1 4 8-3 1-4-8-4 5V3z"
          fill="currentColor"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/* ───────────────────── Use case Apps ─────────────────────
   Each is a bespoke kiosk design tuned to its data type:
   - Sales:    Stripe-style dashboard with sparkline + KPI strip
   - Cafe:     editorial welcome with featured pour-over + menu
   - AllHands: Apple-keynote slide with gradient hero metric
   - Patient:  clinic check-in with "Now serving" banner

   They share visual primitives (rounded cards, ring borders, tabular
   nums, subtle shadows) so the demos feel like the same product family
   without looking templated. */

/** Sales dashboard — spreadsheet → live sales display. */
function SalesApp({ compact, animated }: AppProps): JSX.Element {
  // 7-day revenue (Mon..Sun) — values in arbitrary units, used for the
  // sparkline shape only. Saturday is the visible peak.
  const data = [42, 56, 68, 52, 78, 92, 70];
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  // viewBox: 100 wide × 32 tall. Map values into [4..28] for headroom.
  const points = data.map((v, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 28 - ((v - min) / range) * 24,
  }));
  const peakIdx = data.indexOf(max);
  const peak = points[peakIdx];
  const polyline = points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  const area = `M${points
    .map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' L')} L100,32 L0,32 Z`;
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div className={cn(
      'relative bg-white',
      compact ? 'p-3' : 'p-5',
    )}>
      {/* Eyebrow + status */}
      <div className="flex items-center justify-between" style={fadeUp(60, animated)}>
        <p className={cn(
          'font-medium uppercase tracking-[0.18em] text-slate-500',
          compact ? 'text-[7px]' : 'text-[10px]',
        )}>
          All locations · 7 days
        </p>
        <div className={cn(
          'inline-flex items-center gap-1 rounded-full bg-emerald-500/10 font-medium text-emerald-600',
          compact ? 'h-3.5 px-1.5 text-[8px]' : 'h-5 px-2 text-[10px]',
        )}>
          <span className={cn('rounded-full bg-emerald-500', compact ? 'h-1 w-1' : 'h-1.5 w-1.5')} />
          Live
        </div>
      </div>

      {/* Hero metric */}
      <div className={cn('flex items-end justify-between', compact ? 'mt-1.5' : 'mt-3')} style={fadeUp(160, animated)}>
        <div>
          <p className={cn(
            'font-bold tabular-nums tracking-[-0.02em] text-slate-900',
            compact ? 'text-[22px] leading-none' : 'text-4xl sm:text-5xl leading-none',
          )}>
            $48,210
          </p>
          <p className={cn('text-slate-500', compact ? 'mt-0.5 text-[8px]' : 'mt-1.5 text-xs')}>
            Revenue · last 7 days
          </p>
        </div>
        <div className={cn(
          'inline-flex items-center gap-0.5 rounded-md bg-emerald-500/10 font-bold tabular-nums text-emerald-600',
          compact ? 'h-4 px-1.5 text-[9px]' : 'h-7 px-2 text-xs',
        )}>
          <TrendingUp className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} strokeWidth={2.5} />
          +24%
        </div>
      </div>

      {/* Sparkline */}
      <div
        className={cn('relative', compact ? 'mt-2 h-9' : 'mt-4 h-16')}
        style={fadeUp(280, animated)}
      >
        <svg className="h-full w-full" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden>
          <defs>
            <linearGradient id="meetSalesGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.32" />
              <stop offset="100%" stopColor="rgb(16, 185, 129)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#meetSalesGrad)" />
          <polyline
            points={polyline}
            fill="none"
            stroke="rgb(16, 185, 129)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {/* Peak marker — HTML so it doesn't get warped by preserveAspectRatio="none" */}
        <div
          aria-hidden
          className={cn(
            'absolute rounded-full bg-emerald-500 ring-2 ring-white shadow-sm',
            compact ? 'h-1.5 w-1.5' : 'h-2 w-2',
          )}
          style={{
            left: `calc(${peak.x}% - ${compact ? 3 : 4}px)`,
            top: `calc(${(peak.y / 32) * 100}% - ${compact ? 3 : 4}px)`,
          }}
        />
      </div>

      {/* Day labels */}
      <div className={cn('flex justify-between', compact ? 'mt-0.5 px-0.5' : 'mt-1 px-1')}>
        {days.map((d, i) => (
          <span
            key={i}
            className={cn(
              'font-medium tabular-nums',
              i === peakIdx ? 'text-emerald-600' : 'text-slate-400',
              compact ? 'text-[7px]' : 'text-[9px]',
            )}
          >
            {d}
          </span>
        ))}
      </div>

      {/* KPI strip */}
      <div
        className={cn(
          'flex items-center justify-between rounded-lg bg-white shadow-sm ring-1 ring-slate-900/[0.06]',
          compact ? 'mt-2 px-2 py-1.5' : 'mt-3 px-3 py-2.5',
        )}
        style={fadeUp(420, animated)}
      >
        {[
          { v: '1,284',     l: 'Units sold' },
          { v: '$37.50',    l: 'Avg order' },
          { v: 'Northeast', l: 'Top region' },
        ].map((kpi) => (
          <div key={kpi.l} className="flex flex-col">
            <span className={cn('font-semibold tabular-nums text-slate-900', compact ? 'text-[10px]' : 'text-sm')}>
              {kpi.v}
            </span>
            <span className={cn('text-slate-500', compact ? 'text-[7px]' : 'text-[10px]')}>
              {kpi.l}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cafe lobby welcome — company URL → branded welcome screen.
 *  Structured to mirror Sales' premium DNA: eyebrow + status pill, hero +
 *  side-pill, a 3-card "visualization" lineup, and a final KPI stat strip. */
function CafeApp({ compact, animated }: AppProps): JSX.Element {
  const drinks = [
    { tag: 'Pour-over', name: 'Yirgacheffe', price: '$5.00' },
    { tag: 'Signature', name: 'Honey Latte', price: '$5.00' },
    { tag: 'Cold',      name: 'Cold Brew',   price: '$4.50' },
  ];

  return (
    <div className={cn(
      'relative bg-white',
      compact ? 'p-3' : 'p-5',
    )}>
      {/* Eyebrow + open pill */}
      <div className="flex items-center justify-between" style={fadeUp(60, animated)}>
        <div className="flex items-center gap-1.5">
          <div className={cn(
            'flex items-center justify-center rounded-md bg-amber-700 text-white shadow-sm',
            compact ? 'h-3.5 w-3.5' : 'h-5 w-5',
          )}>
            <Coffee className={compact ? 'h-2 w-2' : 'h-3 w-3'} strokeWidth={2.25} />
          </div>
          <span className={cn(
            'font-bold uppercase tracking-[0.22em] text-slate-900',
            compact ? 'text-[7px]' : 'text-[10px]',
          )}>
            Acme Coffee
          </span>
        </div>
        <div className={cn(
          'inline-flex items-center gap-1 rounded-full bg-amber-700/10 font-medium text-amber-700',
          compact ? 'h-3.5 px-1.5 text-[8px]' : 'h-5 px-2 text-[10px]',
        )}>
          <span className={cn('rounded-full bg-amber-600', compact ? 'h-1 w-1' : 'h-1.5 w-1.5')} />
          Open · until 8pm
        </div>
      </div>

      {/* Hero — welcome + featured drink pill */}
      <div
        className={cn('flex items-end justify-between', compact ? 'mt-1.5' : 'mt-3')}
        style={fadeUp(160, animated)}
      >
        <div>
          <p className={cn(
            'font-bold tracking-[-0.02em] text-slate-900',
            compact ? 'text-[22px] leading-none' : 'text-4xl leading-none',
          )}>
            Good afternoon.
          </p>
          <p className={cn('text-slate-500', compact ? 'mt-0.5 text-[8px]' : 'mt-1.5 text-xs')}>
            Pour-over today · Ethiopian Yirgacheffe
          </p>
        </div>
        <div className={cn(
          'inline-flex items-center rounded-md bg-amber-700/10 font-bold tabular-nums text-amber-700',
          compact ? 'h-4 px-1.5 text-[9px]' : 'h-7 px-2.5 text-xs',
        )}>
          $5
        </div>
      </div>

      {/* Featured drinks lineup — visual anchor (analogous to Sales sparkline) */}
      <div
        className={cn('grid grid-cols-3', compact ? 'mt-2 gap-1.5' : 'mt-4 gap-2')}
        style={fadeUp(280, animated)}
      >
        {drinks.map((d) => (
          <div
            key={d.name}
            className={cn(
              'rounded-lg border border-amber-700/15 bg-amber-700/[0.04]',
              compact ? 'px-1.5 py-1' : 'px-2.5 py-2',
            )}
          >
            <p className={cn(
              'font-medium uppercase tracking-wider text-amber-700/80',
              compact ? 'text-[6px]' : 'text-[9px]',
            )}>
              {d.tag}
            </p>
            <p className={cn(
              'font-semibold text-slate-900',
              compact ? 'mt-0.5 text-[9px]' : 'mt-0.5 text-[12px]',
            )}>
              {d.name}
            </p>
            <p className={cn(
              'font-bold tabular-nums text-slate-700',
              compact ? 'text-[8px]' : 'text-[11px]',
            )}>
              {d.price}
            </p>
          </div>
        ))}
      </div>

      {/* KPI strip */}
      <div
        className={cn(
          'flex items-center justify-between rounded-lg bg-white shadow-sm ring-1 ring-slate-900/[0.06]',
          compact ? 'mt-2 px-2 py-1.5' : 'mt-3 px-3 py-2.5',
        )}
        style={fadeUp(420, animated)}
      >
        {[
          { v: '★ 4.9',  l: '1.2k reviews' },
          { v: '12 min', l: 'Avg wait' },
          { v: 'Free',   l: 'ACME-GUEST WiFi' },
        ].map((kpi) => (
          <div key={kpi.l} className="flex flex-col">
            <span className={cn('font-semibold tabular-nums text-slate-900', compact ? 'text-[10px]' : 'text-sm')}>
              {kpi.v}
            </span>
            <span className={cn('text-slate-500', compact ? 'text-[7px]' : 'text-[10px]')}>
              {kpi.l}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Q4 all-hands slide — deck → office screens. Apple-keynote-style. */
function AllHandsApp({ compact, animated }: AppProps): JSX.Element {
  return (
    <div className={cn(
      'relative bg-white',
      compact ? 'p-3' : 'p-5',
    )}>
      {/* Slide chrome */}
      <div className="flex items-center justify-between" style={fadeUp(60, animated)}>
        <p className={cn(
          'font-bold uppercase tracking-[0.22em] text-purple-600',
          compact ? 'text-[7px]' : 'text-[10px]',
        )}>
          Q4 · Company Update
        </p>
        <p className={cn(
          'font-medium tabular-nums text-slate-400',
          compact ? 'text-[7px]' : 'text-[10px]',
        )}>
          01 / 06
        </p>
      </div>

      {/* Hero metric — gradient text for keynote feel */}
      <p
        className={cn(
          'bg-gradient-to-br from-purple-600 to-indigo-500 bg-clip-text font-bold tracking-[-0.04em] text-transparent',
          compact ? 'mt-1.5 text-[40px] leading-none' : 'mt-1.5 text-6xl leading-none',
        )}
        style={fadeUp(180, animated)}
      >
        +24%
      </p>
      <p
        className={cn(
          'font-semibold text-slate-900',
          compact ? 'mt-0.5 text-[11px]' : 'mt-1.5 text-base',
        )}
        style={fadeUp(260, animated)}
      >
        Revenue growth
      </p>
      <p
        className={cn('text-slate-500', compact ? 'text-[8px]' : 'text-[11px]')}
        style={fadeUp(320, animated)}
      >
        across all product lines · vs Q3
      </p>

      {/* Stat strip */}
      <div
        className={cn(
          'grid grid-cols-3 divide-x divide-slate-200/80 border-t border-slate-200/80',
          compact ? 'mt-2 pt-2' : 'mt-2.5 pt-2',
        )}
        style={fadeUp(440, animated)}
      >
        {[
          { v: '67',     l: 'Customer NPS', sub: '↑ from 58' },
          { v: '+18',    l: 'New hires',    sub: '4 teams' },
          { v: 'Mar 12', l: 'Beta launch',  sub: 'on schedule' },
        ].map((s) => (
          <div key={s.l} className={cn('first:pl-0 last:pr-0', compact ? 'px-1.5' : 'px-3')}>
            <p className={cn(
              'font-bold tabular-nums tracking-tight text-slate-900',
              compact ? 'text-[12px]' : 'text-xl',
            )}>
              {s.v}
            </p>
            <p className={cn(
              'font-medium text-slate-700',
              compact ? 'mt-0.5 text-[7px]' : 'mt-0.5 text-[11px]',
            )}>
              {s.l}
            </p>
            <p className={cn(
              'text-slate-400',
              compact ? 'text-[6px]' : 'text-[9px]',
            )}>
              {s.sub}
            </p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        className={cn(
          'flex items-center justify-between border-t border-slate-200/80',
          compact ? 'mt-2 pt-1.5' : 'mt-2 pt-1.5',
        )}
        style={fadeUp(580, animated)}
      >
        <div className="flex items-center gap-1.5 text-slate-600">
          <div className={cn(
            'flex items-center justify-center rounded-md bg-purple-500/10 text-purple-600',
            compact ? 'h-3.5 w-3.5' : 'h-5 w-5',
          )}>
            <Calendar className={compact ? 'h-2 w-2' : 'h-3 w-3'} strokeWidth={2.25} />
          </div>
          <span className={cn(
            'font-medium text-slate-700',
            compact ? 'text-[7px]' : 'text-[11px]',
          )}>
            All-hands · Friday 3pm
          </span>
        </div>
        <span className={cn('text-slate-400', compact ? 'text-[7px]' : 'text-[10px]')}>
          ↻ 30s
        </span>
      </div>
    </div>
  );
}

/** Patient waiting room — clinic check-in display.
 *  Mirrors Sales' DNA: eyebrow + Live pill, big hero ticket + side-pill,
 *  an "Up next" queue list as the visualization, and a KPI strip. */
function PatientApp({ compact, animated }: AppProps): JSX.Element {
  const upNext = [
    { num: '#043', name: 'Dr. Chen',   spec: 'Pediatrics',  wait: '5 min'  },
    { num: '#044', name: 'Dr. Rivera', spec: 'Dermatology', wait: '12 min' },
    { num: '#045', name: 'Dr. Patel',  spec: 'Cardiology',  wait: '18 min' },
  ];

  return (
    <div className={cn(
      'relative bg-white',
      compact ? 'p-3' : 'p-5',
    )}>
      {/* Eyebrow + Live pill */}
      <div className="flex items-center justify-between" style={fadeUp(60, animated)}>
        <p className={cn(
          'font-medium uppercase tracking-[0.18em] text-slate-500',
          compact ? 'text-[7px]' : 'text-[10px]',
        )}>
          Northside Family Clinic
        </p>
        <div className={cn(
          'inline-flex items-center gap-1 rounded-full bg-emerald-500/10 font-medium text-emerald-600',
          compact ? 'h-3.5 px-1.5 text-[8px]' : 'h-5 px-2 text-[10px]',
        )}>
          <span className={cn('rounded-full bg-emerald-500', compact ? 'h-1 w-1' : 'h-1.5 w-1.5')} />
          Live
        </div>
      </div>

      {/* Hero — now serving ticket + room pill */}
      <div
        className={cn('flex items-end justify-between', compact ? 'mt-1.5' : 'mt-3')}
        style={fadeUp(160, animated)}
      >
        <div>
          <p className={cn(
            'font-bold tabular-nums tracking-[-0.02em] text-slate-900',
            compact ? 'text-[22px] leading-none' : 'text-4xl sm:text-5xl leading-none',
          )}>
            #042
          </p>
          <p className={cn('text-slate-500', compact ? 'mt-0.5 text-[8px]' : 'mt-1.5 text-xs')}>
            Now serving · Dr. Patel · Cardiology
          </p>
        </div>
        <div className={cn(
          'inline-flex items-center rounded-md bg-purple-500/10 font-bold tabular-nums text-purple-700',
          compact ? 'h-4 px-1.5 text-[9px]' : 'h-7 px-2.5 text-xs',
        )}>
          Room 3
        </div>
      </div>

      {/* Up next queue — visualization (analogous to Sales sparkline) */}
      <div className={cn(compact ? 'mt-2' : 'mt-4')} style={fadeUp(280, animated)}>
        <div className="flex items-center justify-between">
          <p className={cn(
            'font-medium uppercase tracking-[0.18em] text-slate-400',
            compact ? 'text-[6px]' : 'text-[9px]',
          )}>
            Up next
          </p>
          <p className={cn(
            'font-medium text-slate-400',
            compact ? 'text-[6px]' : 'text-[9px]',
          )}>
            ETA
          </p>
        </div>
        <div className={cn('space-y-1.5', compact ? 'mt-1' : 'mt-1.5')}>
          {upNext.map((q, i) => {
            const isNext = i === 0;
            // Position-based hierarchy carries the "progress" signal —
            // next-up ticket gets the purple-indigo gradient + bolder type;
            // later tickets fade to slate. No bars, no clutter.
            return (
              <div key={q.num} className="flex items-center gap-2.5">
                <span className={cn(
                  'font-bold tabular-nums',
                  compact ? 'w-7 text-[8px]' : 'w-9 text-[12px]',
                  isNext
                    ? 'bg-gradient-to-br from-purple-600 to-indigo-500 bg-clip-text text-transparent'
                    : 'text-slate-500',
                )}>
                  {q.num}
                </span>
                <span className={cn(
                  'flex-1 truncate font-medium',
                  compact ? 'text-[9px]' : 'text-[12px]',
                  isNext ? 'text-slate-900' : 'text-slate-700',
                )}>
                  {q.name}
                </span>
                <span className={cn(
                  'truncate text-slate-500',
                  compact ? 'text-[7px]' : 'text-[10px]',
                )}>
                  {q.spec}
                </span>
                <span className={cn(
                  'text-right font-medium tabular-nums',
                  compact ? 'w-9 text-[7px]' : 'w-10 text-[11px]',
                  isNext ? 'text-purple-700' : 'text-slate-500',
                )}>
                  {q.wait}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI strip */}
      <div
        className={cn(
          'flex items-center justify-between rounded-lg bg-white shadow-sm ring-1 ring-slate-900/[0.06]',
          compact ? 'mt-2 px-2 py-1.5' : 'mt-3 px-3 py-2.5',
        )}
        style={fadeUp(540, animated)}
      >
        {[
          { v: '12 min', l: 'Avg wait' },
          { v: '7',      l: 'In queue' },
          { v: '3 of 4', l: 'Doctors on' },
        ].map((kpi) => (
          <div key={kpi.l} className="flex flex-col">
            <span className={cn('font-semibold tabular-nums text-slate-900', compact ? 'text-[10px]' : 'text-sm')}>
              {kpi.v}
            </span>
            <span className={cn('text-slate-500', compact ? 'text-[7px]' : 'text-[10px]')}>
              {kpi.l}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────── Scroll progress for the pinned card stack ────────────────
 *
 * Returns 0→1 as the tall track scrolls past while the stage is pinned: 0 when
 * the track top reaches the top of the scrollport, 1 when its bottom does.
 * Drives the continuous "deck" reveal. rAF-throttled; capture-phase scroll so
 * it also fires if an inner element scrolls (works regardless of scroll root). */
function useScrollProgress(): {
  trackRef: React.RefObject<HTMLDivElement>;
  progress: number;
} {
  const trackRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    let queued = false;
    const update = () => {
      queued = false;
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const scrollable = rect.height - vh;
      const p = scrollable > 0 ? -rect.top / scrollable : 0;
      setProgress(Math.min(1, Math.max(0, p)));
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      raf = requestAnimationFrame(update);
    };
    update();
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    window.addEventListener('scroll', onScroll, opts);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, opts);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return { trackRef, progress };
}

/* ──────────────── Inline "How it works" section ────────────────
 *
 * Sticky-left / pinned-deck-right: the three steps stay pinned on the left. On
 * the right, all scene cards START stacked on top of each other (a deck). As
 * the visitor scrolls the tall track, scroll progress continuously peels the
 * front card up and out while the next card rises from underneath to the front
 * — the left step highlights in sync. A single, generic use case is shown. */
export function HowItWorksSection({
  thumbnailUrl,
  promptText,
  screenNames,
}: {
  /** Category thumbnail (a real deployed app screenshot). When set, the build +
   *  publish steps show this image. */
  thumbnailUrl?: string;
  /** Prompt that produced the thumbnail — typed in the Idea step. */
  promptText?: string;
  /** Endpoint-screen names for the publish step. */
  screenNames?: string[];
}): JSX.Element {
  // Generic fallback use case, used only when no thumbnail is provided.
  const useCase = findUseCaseById('sales') ?? USE_CASES[0];
  const { trackRef, progress } = useScrollProgress();
  // Continuous "front" position across the deck (0 → count-1); `front` is the
  // card currently in focus (drives the left highlight + scene animation).
  // The last ~18% of the track is reserved as end-dwell: the final card reaches
  // the front before progress hits 1 and then holds centered while still pinned,
  // so the scroll-out doesn't yank it away the instant it arrives.
  const SCROLL_SPAN = 0.82;
  const f = Math.min(1, progress / SCROLL_SPAN) * (STEPS.length - 1);
  const front = Math.round(f);

  return (
    // The tall track is the scroll driver; the steps + deck live in ONE pinned
    // stage as a single vertically-centered group — no separate sticky elements
    // competing.
    <div ref={trackRef} style={{ height: `${STEPS.length * 85}vh` }}>
      <div className="sticky top-0 flex h-screen flex-col justify-center">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8">
          {/* LEFT — steps; the front card's step highlights in sync. */}
          <div className="lg:col-span-4">
            <ol className="flex flex-col gap-6 lg:gap-0">
              {STEPS.map((step, i) => {
                const isActive = i === front;
                const isLast = i === STEPS.length - 1;
                return (
                  <li key={step.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors duration-300',
                          isActive
                            ? 'border-purple-600 bg-purple-600 text-white'
                            : 'border-border bg-card text-muted-foreground',
                        )}
                      >
                        {i + 1}
                      </span>
                      {!isLast && (
                        <span className="relative my-1 hidden w-px flex-1 overflow-hidden bg-border lg:block">
                          <span
                            className="absolute inset-x-0 top-0 bg-purple-600 transition-[height] duration-300"
                            style={{ height: i < front ? '100%' : '0%' }}
                          />
                        </span>
                      )}
                    </div>
                    <div
                      className={cn(
                        'transition-opacity duration-300 lg:pb-12',
                        isActive ? 'opacity-100' : 'lg:opacity-40',
                      )}
                    >
                      <h3
                        className={cn(
                          'text-xl font-semibold tracking-tight transition-colors duration-300 sm:text-2xl',
                          isActive ? 'text-foreground' : 'text-muted-foreground',
                        )}
                      >
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* RIGHT — the deck. Cards START stacked on each other; `pos = i - f`
              is each card's position in the deck (~0 = front, >0 = underneath
              peeking up from below, <0 = sliding up and out the top). Pure
              slide (no fade). */}
          <div className="lg:col-span-8">
            <div className="relative aspect-[16/10] w-full">
              {STEPS.map((step, i) => {
                const pos = i - f;
                const ahead = Math.max(0, pos); // underneath the front (waiting)
                const past = Math.min(0, pos); // ≤ 0 → in front and leaving
                const slideUpPct = past * 120; // slide UP and out the top
                const peekDownPx = ahead * 22; // stacked cards peek up from below the front
                const scale = 1 - ahead * 0.05;
                return (
                  <div
                    key={step.id}
                    className="absolute inset-0 overflow-hidden rounded-2xl bg-gradient-to-br from-white to-zinc-50 shadow-2xl ring-1 ring-black/5 dark:from-zinc-900 dark:to-zinc-950 dark:ring-white/10"
                    style={{
                      transform: `translate3d(0, calc(${slideUpPct}% + ${peekDownPx}px), 0) scale(${scale})`,
                      zIndex: Math.round((STEPS.length - pos) * 100),
                      transformOrigin: 'bottom center',
                      willChange: 'transform',
                    }}
                  >
                    <SceneByIndex
                      key={`${useCase.id}-${i}`}
                      index={i}
                      active={i === front}
                      useCase={useCase}
                      thumbnailUrl={thumbnailUrl}
                      promptText={promptText}
                      screenNames={screenNames}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
